import React, { useEffect, useRef, useState } from 'react';
import { useStyletron } from 'baseui';
import useTutorial from '../../../../providers/hooks/use-tutorials';
import { VIDEO_TUTORIAL_CONFIG } from '../../../../tutorials/video-tutorial';
import { ACTION_TYPE } from '../../constants';
import { SequenceOverrideConfirmationModal } from '../sequence-override-confirmation-modal';
import { SequenceActions } from './annotation-timeline-config-sub-components/sequence-actions';
import { useSequenceActions } from './hooks/use-sequence-actions';
import { useSequenceValidation } from './hooks/use-sequence-validation';
import { AnnotationSequence } from './annotation-sequence';
import type { AnnotationTimelineConfigPropsT } from './annotation-timeline-config';
import { SEQUENCE_MODAL_TYPE } from './constants';
type PropsT = Omit<AnnotationTimelineConfigPropsT, 'onPropagationMethodChange'> & {
  totalFrames: number;
};
export const AddNewSequence = ({
  annotationId,
  maxFrames,
  currentFrame,
  hasManualKeyFrameInRange,
  annotationSequence,
  annotationLabel,
  onChange,
  actionConfig,
  setTrackedSequence,
  timeDisplayMode,
  frameRate,
  totalFrames
}: PropsT) => {
  const [css, theme] = useStyletron();
  const {
    addStep,
    currentActiveTutorial,
    next
  } = useTutorial();
  const [newRange, setNewRange] = useState({
    start: currentFrame,
    end: maxFrames - 1
  });
  const isTrackingMode = actionConfig?.includes(ACTION_TYPE.TRACK);
  const {
    isSequenceValid,
    isTrackingValid,
    validationError
  } = useSequenceValidation({
    newRange,
    maxFrames,
    isTrackingMode
  });
  const {
    isTracking,
    isInterpolating,
    showTrackOverrideModal,
    showInterpolateOverrideModal,
    handleTrackNextFrames,
    handleConfirmTrackOverride,
    handleCancelTrackOverride,
    handleInterpolate,
    handleConfirmInterpolateOverride,
    handleCancelInterpolateOverride
  } = useSequenceActions({
    annotationId,
    hasManualKeyFrameInRange,
    annotationSequence,
    onChange,
    setTrackedSequence,
    newRange
  });
  const containerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (currentActiveTutorial === VIDEO_TUTORIAL_CONFIG.annotationTimeline.id) {
      addStep({
        ...VIDEO_TUTORIAL_CONFIG.lastInterpolationFrame,
        getTarget: () => containerRef.current
      }, VIDEO_TUTORIAL_CONFIG.annotationTimeline.id);
    }
  }, [addStep, currentActiveTutorial]);
  const handleInterpolateWithTutorial = async () => {
    await handleInterpolate();
    next(VIDEO_TUTORIAL_CONFIG.lastInterpolationFrame.id);
  };
  return <div ref={containerRef} className={css({
    backgroundColor: theme.colors.backgroundPrimary,
    fontFamily: 'Uber Move'
  })} data-testid="annotation-timeline-config" data-action-config={JSON.stringify(actionConfig)}>
      <AnnotationSequence setNewRange={setNewRange} currentFrame={currentFrame} maxFrames={maxFrames} timeDisplayMode={timeDisplayMode} frameRate={frameRate} totalFrames={totalFrames} />

      <SequenceActions actionConfig={actionConfig} isTracking={isTracking} isInterpolating={isInterpolating} isSequenceValid={isSequenceValid} isTrackingValid={isTrackingValid} validationError={validationError} onTrack={handleTrackNextFrames} onInterpolate={handleInterpolateWithTutorial} />

      {/* TODO: use Modal Provider to show these modals */}
      <SequenceOverrideConfirmationModal isOpen={showTrackOverrideModal} onConfirm={handleConfirmTrackOverride} onCancel={handleCancelTrackOverride} startFrame={newRange.start} endFrame={newRange.end} labelTitle={annotationLabel.title} isLoading={isTracking} type={SEQUENCE_MODAL_TYPE.TRACK} />

      <SequenceOverrideConfirmationModal isOpen={showInterpolateOverrideModal} onConfirm={handleConfirmInterpolateOverride} onCancel={handleCancelInterpolateOverride} startFrame={newRange.start} endFrame={newRange.end} labelTitle={annotationLabel.title} isLoading={false} type={SEQUENCE_MODAL_TYPE.INTERPOLATE} />
    </div>;
};